import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

const Navbar = ({ onOpenDonate, onOpenContact }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [textIndex, setTextIndex] = useState(0)
  const [activeSection, setActiveSection] = useState('hero')
  const navigate = useNavigate()
  const location = useLocation()

  const navItems = [
    { label: 'Home', id: 'hero' },
    { label: 'Welfare Activities', id: 'welfare-activities', isRoute: true, path: '/welfare-activities' },
    { label: 'Awareness', id: 'awareness', isRoute: true, path: '/awareness' },
    { label: 'Adoption', id: 'adoption' },
    { label: 'Volunteer', id: 'volunteer' },
    { label: 'Contact', id: 'contact' },
  ]

  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % 2)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  // Highlight active section on scroll (Scrollspy)
  useEffect(() => {
    if (location.pathname !== '/') return

    const handleScroll = () => {
      const sections = ['hero', 'adoption', 'volunteer', 'awareness']
      const scrollPosition = window.scrollY + 250 // offset for navbar height + buffer

      for (const section of sections) {
        const el = document.getElementById(section)
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY
          const height = el.offsetHeight
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section)
            break
          }
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    // Run once initially to capture initial load scroll position
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [location.pathname])

  // Pathname active override
  useEffect(() => {
    if (location.pathname === '/awareness') {
      setActiveSection('awareness')
    } else if (location.pathname === '/welfare-activities' || location.pathname === '/welfare' || location.pathname === '/activities') {
      setActiveSection('welfare-activities')
    } else if (location.pathname !== '/') {
      setActiveSection('')
    }
  }, [location.pathname])

  const handleNavItemClick = (e, item) => {
    e.preventDefault()
    setIsMobileMenuOpen(false)

    if (item.isRoute) {
      navigate(item.path)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const id = typeof item === 'string' ? item : item.id

    if (id === 'contact') {
      onOpenContact()
      return
    }

    if (location.pathname !== '/') {
      navigate(`/#${id}`)
      return
    }

    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const isActive = (id) => activeSection === id

  return (
    <div className="fixed top-0 left-0 right-0 z-40 bg-transparent">
      {/* Outer Wrapper */}
      <div className="px-3.5 sm:px-6 lg:px-8 2xl:px-12 pt-3 sm:pt-4 2xl:pt-6">
        {/* Outer Split Wrapper */}
        <div className="max-w-8xl mx-auto flex items-start justify-between gap-4">

          {/* Capsule 1: Logo, Desktop Nav Links & Attached Mobile Dropdown */}
          <div className={`bg-white/95 backdrop-blur-md shadow-lg shadow-slate-900/5 border border-slate-100 flex flex-col lg:flex-row lg:items-center justify-between lg:justify-start gap-0 lg:gap-5 xl:gap-6 2xl:gap-8 px-4 sm:px-5 lg:px-5 2xl:px-8 py-2 sm:py-2.5 2xl:py-3.5 transition-all duration-300 w-full lg:w-auto ${
            isMobileMenuOpen ? 'rounded-[24px] sm:rounded-[32px]' : 'rounded-[36px] 2xl:rounded-[44px]'
          }`}>
            {/* Top Bar row inside capsule */}
            <div className="flex items-center justify-between w-full lg:w-auto">
              {/* Logo */}
              <a
                href="#hero"
                onClick={(e) => handleNavItemClick(e, { id: 'hero' })}
                className="flex items-center gap-2 focus:outline-none cursor-pointer pr-3.5 lg:pr-4 2xl:pr-6 border-r border-slate-200"
              >
                <span
                  className="text-2xl sm:text-3xl 2xl:text-4xl tracking-tight text-slate-900 font-normal hover:text-brand-green-950 transition-colors"
                  style={{ fontFamily: 'var(--app-font-heading)' }}
                >
                  CAWT
                </span>
              </a>

              {/* Desktop Nav items */}
              <div className="hidden lg:flex items-center gap-4 lg:gap-5 xl:gap-6 2xl:gap-8 pl-3.5 lg:pl-4 xl:pl-5 2xl:pl-7">
                {navItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.path || item.url || `#${item.id}`}
                    target={item.url ? "_blank" : undefined}
                    rel={item.url ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      if (!item.url) handleNavItemClick(e, item)
                    }}
                    className={`text-sm xs:text-base sm:text-lg md:text-xl text-brand-orange-700 font-semibold leading-relaxed  transition-all relative py-1 focus:outline-none cursor-pointer whitespace-nowrap ${!item.url && item.id !== 'contact' && isActive(item.id)
                        ? 'text-brand-orange-700 text-base lg:text-base xl:text-lg 2xl:text-xl font-normal'
                        : 'text-slate-700 hover:text-slate-950 font-semibold'
                      }`}
                    style={!item.url && item.id !== 'contact' && isActive(item.id) ? { fontFamily: 'var(--app-font-heading)' } : {}}
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              {/* Mobile / iPad hamburger menu toggle with 44px min touch target */}
              <div className="flex lg:hidden">
                <button
                  type="button"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-700 hover:text-brand-orange-600 rounded-xl transition-colors focus:outline-none cursor-pointer"
                  aria-label="Toggle navigation menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  {isMobileMenuOpen ? (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Dropdown Attached Directly Inside Navbar Capsule */}
            {isMobileMenuOpen && (
              <div className="lg:hidden w-full pt-3 mt-2 border-t border-slate-100 space-y-1 animate-fade-in text-left">
                {navItems.map((item) => (
                  <a
                    key={item.id}
                    href={item.path || item.url || `#${item.id}`}
                    target={item.url ? "_blank" : undefined}
                    rel={item.url ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                      if (!item.url) handleNavItemClick(e, item)
                      else setIsMobileMenuOpen(false)
                    }}
                    className={`block w-full text-left px-3.5 py-2 rounded-xl text-sm sm:text-base transition-colors ${
                      !item.url && item.id !== 'contact' && isActive(item.id)
                        ? 'bg-brand-orange-50 text-brand-orange-700 font-normal'
                        : 'text-slate-700 font-semibold hover:bg-slate-50'
                    }`}
                    style={!item.url && item.id !== 'contact' && isActive(item.id) ? { fontFamily: 'var(--app-font-heading)', fontSize: '1.2rem' } : {}}
                  >
                    {item.label}
                  </a>
                ))}

                <div className="pt-2.5 pb-1 border-t border-slate-100">
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      onOpenDonate()
                    }}
                    className="relative w-full py-2.5 bg-gradient-to-r from-brand-orange-700 via-brand-orange-600 to-brand-orange-700 bg-[length:200%_auto] animate-gradient-shift hover:shadow-md text-white text-xs sm:text-sm font-bold rounded-xl overflow-hidden flex items-center justify-center h-[38px] focus:outline-none transition-all group border border-brand-orange-600/30 cursor-pointer"
                  >
                    {/* Shimmer sheen overlay */}
                    <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer pointer-events-none" />

                    {/* Dynamic Text Ticker Container */}
                    <div className="relative w-full h-full flex items-center justify-center">
                      <span
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out whitespace-nowrap ${
                          textIndex === 0
                            ? 'opacity-100 transform translate-y-0'
                            : 'opacity-0 transform -translate-y-4 pointer-events-none'
                        }`}
                      >
                        Donate Now
                      </span>
                      <span
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out whitespace-nowrap ${
                          textIndex === 1
                            ? 'opacity-100 transform translate-y-0'
                            : 'opacity-0 transform translate-y-4 pointer-events-none'
                        } gap-1.5`}
                      >
                        Sponsor a Pet <svg className="w-3.5 h-3.5 fill-current inline-block" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Capsule 2: Sponsor / Donate Action (Desktop & iPad Pro) */}
          <div className="hidden lg:flex bg-white/95 backdrop-blur-md rounded-[36px] 2xl:rounded-[44px] shadow-lg shadow-slate-900/5 border border-slate-100 p-1.5 2xl:p-2 transition-all">
            <button
              onClick={onOpenDonate}
              className="relative px-5 lg:px-5 xl:px-6 2xl:px-8 py-2 bg-gradient-to-r from-brand-orange-700 via-brand-orange-600 to-brand-orange-700 bg-[length:200%_auto] animate-gradient-shift hover:shadow-lg shadow-brand-orange-600/20 text-white text-xs sm:text-sm 2xl:text-base font-bold rounded-full overflow-hidden flex items-center justify-center min-w-[135px] lg:min-w-[135px] xl:min-w-[150px] 2xl:min-w-[180px] h-[38px] 2xl:h-[46px] focus:outline-none transition-all group cursor-pointer transform hover:-translate-y-0.5"
            >
              {/* Shimmer sheen overlay */}
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer pointer-events-none" />

              {/* Dynamic Text Ticker Container */}
              <div className="relative w-full h-full flex items-center justify-center">
                <span
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out whitespace-nowrap ${textIndex === 0
                      ? 'opacity-100 transform translate-y-0'
                      : 'opacity-0 transform -translate-y-4 pointer-events-none'
                    }`}
                >
                  Donate Now
                </span>
                <span
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out whitespace-nowrap ${textIndex === 1
                      ? 'opacity-100 transform translate-y-0'
                      : 'opacity-0 transform translate-y-4 pointer-events-none'
                    } gap-1.5`}
                >
                  Sponsor a Pet <svg className="w-3.5 h-3.5 fill-current inline-block" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                </span>
              </div>
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}

export default Navbar
